import React from 'react';
import { clsx } from 'clsx';
import styles from './Section.module.scss';
import TooltipIcon from '@/components/svg/Tooltip';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { PersonaKycStatus } from '@/types/client';

export const CenterWrapper: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  return <div className={styles.centerWrapper}>{children}</div>;
};

export const ActionLink: React.FC<{
  url: string;
  newTab?: boolean;
  label: string;
  disabled?: boolean;
  onClick?: () => void;
  dataCy: string;
}> = ({ url, newTab, label, disabled, onClick, dataCy }) => {
  if (disabled === true) {
    return (
      <div
        data-cy={dataCy}
        className={clsx([styles.actionLink, styles.actionLinkDisabled])}
      >
        {label}
      </div>
    );
  } else {
    return (
      <a
        className={styles.actionLink}
        data-cy={dataCy}
        href={url}
        onClick={(e) => {
          e.stopPropagation();
          if (onClick) onClick();
        }}
        rel="noreferrer"
        target={newTab ? '_blank' : ''}
      >
        {label}
      </a>
    );
  }
};

export const ActionButton: React.FC<{ onClick: () => void; label: string; disabled?: boolean; dataCy: string }> = ({
  onClick,
  label,
  disabled,
  dataCy,
}) => {
  return (
    <button
      data-cy={dataCy}
      onClick={(e) => {
        onClick();
        e.stopPropagation();
      }}
      className={styles.actionButton}
      disabled={disabled || false}
    >
      {label}
    </button>
  );
};

export const PersonaKycBadge: React.FC = () => {
  return (
    <div className={styles.personaKycBadge}>
      <CheckCircleIcon className={styles.personaKycIcon} />
      <span>Persona KYC</span>
    </div>
  );
};

export const Heading: React.FC<{ label: string; personaKycStatus?: string | null }> = ({ label, personaKycStatus }) => {
  return (
    <div
      className={styles.heading}
      data-cy="Section-Heading"
    >
      <span>{label}</span>
      {personaKycStatus === PersonaKycStatus.APPROVED && <PersonaKycBadge />}
    </div>
  );
};

export const Label: React.FC<{ labelInfo: { label: string; description?: string; onCopy?: () => void } }> = ({
  labelInfo,
}) => {
  const { t } = useTranslation();

  const { label, description, onCopy } = labelInfo;

  if (undefined !== description) {
    return (
      <div
        data-cy={'cell-label'}
        className={styles.label}
      >
        <Tooltip title={description}>
          <div className={styles.tooltipLabel}>
            {label}
            <TooltipIcon className={styles.tooltipIcon} />
          </div>
        </Tooltip>
      </div>
    );
  }

  return (
    <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: 0.5 }}>
      <div
        data-cy={'cell-label'}
        className={styles.label}
      >
        {label}
      </div>

      {!!onCopy && (
        <Tooltip
          title={`${t('item.basic_info.copy_tooltip')}`}
          arrow
        >
          <IconButton
            onClick={onCopy}
            size="small"
            style={{ padding: '2px', marginLeft: '4px' }}
          >
            <ContentCopyIcon style={{ fontSize: '14px' }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export const Title: React.FC<{ label: string }> = ({ label }) => {
  return (
    <div
      data-cy={'section-title'}
      className={styles.title}
    >
      {label}
    </div>
  );
};

export const ValueLink: React.FC<{ url: string; newTab?: boolean; label: string; disabled?: boolean }> = ({
  url,
  newTab,
  label,
  disabled,
}) => {
  return (
    <a
      data-cy={'cell-action'}
      className={clsx([styles.link, disabled ? styles.linkDisabled : ''])}
      href={url}
      rel="noreferrer"
      target={newTab ? '_blank' : ''}
    >
      {label}
    </a>
  );
};

export const ValueButton: React.FC<{ onClick: () => void; label: string; disabled?: boolean; className?: string }> = ({
  onClick,
  label,
  disabled,
  className,
}) => {
  return (
    <button
      data-cy={'cell-action'}
      onClick={onClick}
      className={clsx(styles.button, className)}
      disabled={disabled || false}
    >
      {label}
    </button>
  );
};

export const ValueText: React.FC<{
  kind: 'Text' | 'TextEllipsis';
  label: string;
  background?: string;
  color?: string;
  dataCy?: string;
}> = ({ kind, label, background, color, dataCy }) => {
  const style: React.CSSProperties = {
    background: background ?? 'none',
    width: background ? 'auto' : '100%',
    color: color,
  };

  return (
    <div
      data-cy={dataCy ? `cell-text-${dataCy}` : 'cell-text'}
      style={style}
      className={kind === 'Text' ? styles.text : styles.textEllipsis}
    >
      <div dangerouslySetInnerHTML={{ __html: label }} />
    </div>
  );
};

export const ValueList: React.FC<{ labels: string[] | JSX.Element[] }> = ({ labels }) => {
  return (
    <>
      {labels.map((label, index) => {
        if (typeof label === 'string') {
          return (
            <ValueText
              key={index}
              label={label}
              kind={'Text'}
            />
          );
        }

        return <React.Fragment key={index}>{label}</React.Fragment>;
      })}
    </>
  );
};
